import {useLivePreview as _useLivePreview} from '@payloadcms/live-preview-vue'
import {ref} from 'vue'

/**
 * Wrapper around @payloadcms/live-preview-vue's useLivePreview composable.
 *
 * Automatically resolves the CMS serverURL using the same pattern as usePayloadSdk:
 * - Development: http://localhost:3000
 * - Production: '' (same-origin, assumes CMS and frontend share a domain)
 *
 * Also guards against SSR and null initialData, returning a plain ref in those cases
 * so callers can always use the returned `data` ref unconditionally.
 */
export function useLivePreview<T>(options: {initialData: T | null | undefined; depth?: number}) {
  const route = useRoute()
  const {payloadUrl} = useRuntimeConfig()
  const serverURL = import.meta.dev ? 'http://localhost:3000' : payloadUrl || location.origin

  const isClient = import.meta.client
  const isPreview = Boolean(route.query.preview)

  if (isClient || isPreview) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return _useLivePreview<T extends Record<string, any> ? T : Record<string, any>>({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialData: (options.initialData || {}) as T extends Record<string, any> ? T : Record<string, any>,
      serverURL: serverURL.toString(),
      depth: options.depth ?? 2,
    })
  }

  // on the server return the actual data
  return {data: ref<T | null | undefined>(options.initialData), isLoading: ref(false)}
}
